import { Github, Mail, Youtube, ExternalLink, Code2, GraduationCap, Sparkles, Cpu, Workflow, Boxes, LineChart, Layout, Car, Zap } from "lucide-react";

const SOCIALS = [
  { label: "TikTok", handle: "@codingfever", url: "https://www.tiktok.com/@codingfever", icon: Sparkles },
  { label: "YouTube", handle: "@HNK2005", url: "https://www.youtube.com/@HNK2005", icon: Youtube },
  { label: "Email", handle: "hhnk3693@gmail.com", url: "mailto:hhnk3693@gmail.com", icon: Mail },
];

const PROJECTS = [
  {
    icon: GraduationCap,
    name: "Honors Engineering Mathematics — Math Studio",
    url: "https://learn-mathseng5.netlify.app/",
    tagline: "A studio for matrix thinking, worked examples, and clean math notes.",
    desc: "Use the videos, verified transcripts, and a full PDF of math steps to learn the way it was written on the board. Start with Core Concepts, then move into Worked Examples.",
    accent: "from-indigo-500/20 to-cyan-500/10",
    tag: "Education",
  },
  {
    icon: LineChart,
    name: "Physics Learning Hub — Master Vector Calculus",
    url: "https://learn-vectors.netlify.app/",
    tagline: "Public learning resource for vector calculus.",
    desc: "A comprehensive, free educational platform covering vector algebra, differential operators, coordinate systems, and integral theorems — with interactive tools and AI-powered guidance.",
    accent: "from-cyan-500/20 to-emerald-500/10",
    tag: "Education",
  },
  {
    icon: Workflow,
    name: "HNK Software Solutions",
    url: "https://www.hnksoftwaresolutions.com/",
    tagline: "Custom web solutions, ERP & CRM.",
    desc: "Transform your business with custom web solutions. Expert ERP and CRM implementation, customization, and integration services that drive efficiency and growth.",
    accent: "from-emerald-500/20 to-amber-500/10",
    tag: "Software",
  },
  {
    icon: Sparkles,
    name: "AcademicGen",
    url: "https://academicgen.com/",
    tagline: "AI-powered academic document generator.",
    desc: "AI-powered academic document generator for reports, presentations, conference papers, theses, citations, and AI-generated images with automatic formatting.",
    accent: "from-purple-500/20 to-indigo-500/10",
    tag: "AI / SaaS",
  },
  {
    icon: Code2,
    name: "Introduction to AI and Python Mastery",
    url: "https://learn-ailearninghub.netlify.app/",
    tagline: "A complete learning hub for Python, AI, and algorithmic thinking.",
    desc: "Lessons, videos, exercises, interactive studios, and an AI tutor — all in one place to master Python and AI fundamentals.",
    accent: "from-amber-500/20 to-red-500/10",
    tag: "Education",
  },
  {
    icon: LineChart,
    name: "Graph — Signal Equations & Analysis",
    url: "https://signal-analysis-hub.netlify.app/",
    tagline: "Plot, draw, and decode signal equations.",
    desc: "Learn the building blocks of dynamic systems: unit step, unit impulse, ramp, periodicity, symmetry, and parent functions. Graph keeps lessons, tools, and reference material in one focused workspace.",
    accent: "from-cyan-500/20 to-indigo-500/10",
    tag: "Engineering",
  },
  {
    icon: Boxes,
    name: "WebGL Studio — Build 3D Interfaces",
    url: "https://canvas-webglthreejs.netlify.app/",
    tagline: "Visual scene design + editable Three.js generation.",
    desc: "WebGL Studio combines visual scene design, layered editing, AI assistance, and editable Three.js generation into a single production-ready workflow.",
    accent: "from-purple-500/20 to-cyan-500/10",
    tag: "3D / Tooling",
  },
  {
    icon: Layout,
    name: "UI Builders — Design UIs Visually, Export Real Code",
    url: "https://uibuilders.netlify.app/",
    tagline: "12 Frameworks · Unlimited Exports · 100% Free.",
    desc: "Build interfaces with drag-and-drop, then export production-ready code for 12 frameworks including Python, PyQt6, Java, C#, HTML, React, Vue, Svelte, React Native, and Flutter.",
    accent: "from-indigo-500/20 to-emerald-500/10",
    tag: "Tooling",
  },
  {
    icon: Car,
    name: "Responsive Vision Control System",
    url: "https://automated-parking-control.netlify.app/",
    tagline: "React + TypeScript interactive parkade simulation.",
    desc: "A React + TypeScript (Vite) interactive simulation of an automated parkade control system. Combines a 3D parking scene (react-three-fiber/three.js) with a dashboard for occupancy, status levels (green/amber/red), manual car entry/exit controls, and auto-simulation with live movement/event tracking.",
    note: "Best viewed on laptop or tablet.",
    accent: "from-emerald-500/20 to-cyan-500/10",
    tag: "Simulation",
  },
  {
    icon: Zap,
    name: "Standalone Three.js Parkade Simulator",
    url: "https://automated-parking-systems.netlify.app/",
    tagline: "Single-page Three.js parkade simulator.",
    desc: "Standalone single-page Three.js parkade simulator built in plain HTML/CSS/JavaScript. Renders a full-screen 3D parking environment with camera presets, add/remove car controls, gate and vehicle animations, occupancy thresholds, LED status indicators, and a responsive control-system overlay UI.",
    note: "Best viewed on laptop or tablet.",
    accent: "from-amber-500/20 to-purple-500/10",
    tag: "Simulation",
  },
];

const STACK = ["React 19","FastAPI","Firestore","Firebase Auth","Tailwind CSS","Python","Gemini 2.5 Flash","Humanize AI Pro","Originality.ai","Lemon Squeezy","Cloudflare Pages","Railway"];

export default function About() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500">About</div>
      <h1 className="mt-2 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
        Built for writers under deadline.
      </h1>
      <p className="mt-5 sm:mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-3xl">
        HumanizeAI was born from a simple frustration: rewriting AI-flagged content paragraph-by-paragraph is slow,
        error-prone, and kills momentum. We built a one-shot pipeline that scans every sentence, paraphrases only what's
        flagged, and re-scans automatically — until you hit 0% AI.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
        <div className="glass rounded-2xl p-5 sm:p-6">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400">Mission</div>
          <p className="mt-2 text-sm sm:text-base text-zinc-200">Turn 30-page reports from AI to human in under 60 seconds — so you can ship fast and sound real.</p>
        </div>
        <div className="glass rounded-2xl p-5 sm:p-6">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400">Our promise</div>
          <p className="mt-2 text-sm sm:text-base text-zinc-200">No content stored beyond your control. Your work is yours — we never train on it.</p>
        </div>
      </div>

      <div className="mt-8">
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400">Tech stack</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {STACK.map(t => <span key={t} className="glass px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs">{t}</span>)}
        </div>
      </div>

      {/* About the Author */}
      <section className="mt-16 sm:mt-24">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500">About the Author</div>
        <h2 className="mt-2 text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
          Meet HNK
        </h2>

        <div className="mt-6 sm:mt-8 glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10">
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-7">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden ring-2 ring-indigo-500/40 ring-offset-2 ring-offset-[#0B0C10] glow-primary">
                <img
                  src="https://avatars.githubusercontent.com/u/247853647?v=4"
                  alt="HNK profile"
                  className="w-full h-full object-cover"
                  data-testid="hnk-avatar"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl sm:text-2xl font-bold" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>HNK</h3>
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-[10px] sm:text-xs text-indigo-300 uppercase tracking-widest">Computer Engineer</span>
              </div>
              <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed">
                Computer Engineer specializing in software development, scientific computing, networks, electronics
                and embedded systems — with extensive experience in technical tutoring. Builder of educational platforms,
                interactive simulations, and AI-powered tools.
              </p>

              <div className="mt-5 sm:mt-6">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400">Find me online</div>
                <div className="mt-3 flex flex-wrap gap-2 sm:gap-3">
                  {SOCIALS.map(({ label, handle, url, icon: Icon }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${label.toLowerCase()}`}
                      className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl glass hover:bg-white/[0.08] hover:border-white/[0.15] transition-colors text-xs sm:text-sm"
                    >
                      <Icon size={14} className="text-cyan-300"/>
                      <div className="leading-tight">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
                        <div className="text-zinc-200 font-medium">{handle}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="mt-16 sm:mt-20">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500">Selected Work</div>
        <h2 className="mt-2 text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
          Other things I've built.
        </h2>
        <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-2xl">
          Education platforms, AI tools, 3D simulations, and developer studios — all live, all free to explore.
        </p>

        <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {PROJECTS.map(({ icon: Icon, name, url, tagline, desc, note, accent, tag }, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`project-link-${i}`}
              className="group relative overflow-hidden rounded-2xl p-5 sm:p-6 glass hover:bg-white/[0.05] hover:border-white/[0.15] transition-all hover:-translate-y-0.5"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-30 pointer-events-none`}/>
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/[0.06] border border-white/[0.1] flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-cyan-300 sm:w-[18px] sm:h-[18px]"/>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400 px-1.5 sm:px-2 py-0.5 rounded glass">{tag}</span>
                    <ExternalLink size={14} className="text-zinc-500 group-hover:text-white transition-colors flex-shrink-0"/>
                  </div>
                </div>
                <h3 className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold leading-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
                  {name}
                </h3>
                <p className="mt-1 text-xs sm:text-sm text-cyan-200/80 italic">{tagline}</p>
                <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-zinc-400 leading-relaxed line-clamp-4">{desc}</p>
                {note && <p className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-amber-300/80">⚡ {note}</p>}
                <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-zinc-500 truncate font-mono">{url.replace(/^https?:\/\//, '')}</div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <div className="mt-16 sm:mt-20 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs sm:text-sm text-zinc-300">
          <Cpu size={14} className="text-indigo-300"/> Built with curiosity by HNK
        </div>
      </div>
    </div>
  );
}
